// Orders — /orders. Three tabs (sales, leases, rentals) over one endpoint,
// each with its own column set, its own status chips and its own in-session
// memory of search, filters and sort.
//
// On the shared table. Three things about this page are worth knowing before
// changing it:
//
//  1. THREE module-scope column sets, switched by `activeTab`. The five shared
//     columns are single objects reused across all three arrays, so a tab
//     switch changes the array identity but not any cell renderer's — which is
//     the DataTable's contract 1. The old hardcoded `colSpan={sale ? 6 : 7}` is
//     gone: the vessel derives it from the visible column count.
//  2. PER-TAB SORT MEMORY rides the vessel's `onSortingChange` readback.
//     `key={activeTab}` remounts the table per tab, `initialSorting` seeds it
//     from that tab's saved state, and the readback writes the committed sort
//     back into that tab's slot. The readback fires once on mount with the
//     value it was seeded from, so the echo is a no-op by construction (the
//     writer below bails when nothing moved). The vessel keeps owning the sort
//     state; this page only remembers it.
//  3. Search and status filters stay page-local and per-tab exactly as they
//     were, in `tabStates`. Nothing here is persisted: all of it resets on
//     reload, which is the behavior this page has always had.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import EmptyState from '../components/ui/EmptyState';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';
import { businessToday, formatEventDate } from '../utils/date-utils';
import { INSTANCE } from '../instance';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import { FilterBar, FilterBreak, FilterSearch, Segmented } from '../components/FilterBar';
import { DataTable } from '../components/table/DataTable';
import { OrderType, SalesOrder } from '../Customers/types';
import StatusPill from './StatusPill';
import ReadingBadge from './ReadingBadge';
import OrderStats from './OrderStats';
import { daysFromToday, relativeDays, statTotalsFor } from './orderStats';
import PageLayout from '../components/ui/PageLayout';
import type { OrderStat } from './itemTypes';

/* ------------------------------------------------------------------ */
/*  Status config per order type                                       */
/* ------------------------------------------------------------------ */
const SALE_STATUSES = ['pending', 'confirmed', 'in_production', 'ready', 'shipped', 'cancelled'];
const LEASE_STATUSES = ['pending', 'confirmed', 'shipping', 'delivered', 'awaiting_return', 'returned', 'cancelled'];
const RENTAL_STATUSES = ['pending', 'confirmed', 'shipping', 'delivered', 'awaiting_return', 'returned', 'cancelled'];

/* ------------------------------------------------------------------ */
/*  Tab config                                                         */
/* ------------------------------------------------------------------ */
interface TabConfig {
  key: OrderType;
  label: string;
  statuses: string[];
  /** Done with: out of the Open view and the tab badge (`getOrderCounts`). */
  closed: string[];
  /** Still in progress: a past date on one of these is overdue. */
  stages: string[];
  /** Column id the tab sorts by until the user picks another one. */
  dateCol: 'delivery_date' | 'effective_date';
  dateLabel: string;
  newLink: string;
  newLabel: string;
}

const TABS: TabConfig[] = [
  {
    key: 'sale', label: 'Sales', statuses: SALE_STATUSES,
    closed: ['shipped', 'cancelled'], stages: ['pending', 'confirmed', 'in_production', 'ready'],
    dateCol: 'delivery_date', dateLabel: 'Delivery Date',
    newLink: '/orders/new?type=sale', newLabel: '+ New Sale',
  },
  {
    key: 'lease', label: 'Leases', statuses: LEASE_STATUSES,
    closed: ['returned', 'cancelled'], stages: ['pending', 'confirmed'],
    dateCol: 'effective_date', dateLabel: 'Effective Date',
    newLink: '/orders/new?type=lease', newLabel: '+ New Lease',
  },
  {
    key: 'rental', label: 'Rentals', statuses: RENTAL_STATUSES,
    closed: ['returned', 'cancelled'], stages: ['pending', 'confirmed'],
    dateCol: 'effective_date', dateLabel: 'Dates',
    newLink: '/orders/new?type=rental', newLabel: '+ New Rental',
  },
];

/* ------------------------------------------------------------------ */
/*  Per-tab state (in-memory only — resets on page load)               */
/* ------------------------------------------------------------------ */
interface TabState {
  statusFilters: string[];
  /**
   * The remembered sort. Empty `sortCol` means "not chosen yet", and the tab
   * falls back to its own `dateCol`. Both fields are written only by the
   * table's sort readback, so a tab can only ever hold a column id that its
   * own column set has.
   */
  sortCol: string;
  sortDir: 'asc' | 'desc';
  search: string;
}

const DEFAULT_TAB_STATE: TabState = { statusFilters: [], sortCol: '', sortDir: 'asc', search: '' };

/** The status segment that means "no status filter": every order the tab has
 *  not closed. Not a status, so it is keyed where no row's status can collide
 *  with it. */
const OPEN_STATUSES = '__open__';

/** The row the stats card's next due date points at: the table's own
 *  keyboard-selection look, so it reads as "this one". */
const POINTED_ROW =
  'bg-surface-container-highest shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]';

/* ------------------------------------------------------------------ */
/*  Columns                                                            */
/* ------------------------------------------------------------------ */
const getRowId = (row: SalesOrder) => row.id;

// Module scope, and the shared columns are ONE object each: the three tab
// arrays below reuse them, so switching tabs never hands flexRender a fresh
// cell-renderer identity for a column that did not change.
//
// Sortable columns declare an accessorFn returning the sort-typed value with
// `undefined` for empty (contract 2). Dates stay ISO `YYYY-MM-DD` strings —
// the house comparator's numeric collation orders those chronologically, so no
// column needs to parse a date just to sort by it.
//
// `status`, `end_date` and `creator` carry no accessorFn on purpose: they were
// not clickable before this migration either.
const ORDER_NUMBER_COLUMN: ColumnDef<SalesOrder, any> = {
  id: 'so_number',
  header: 'Order #',
  accessorFn: (o) => o.so_number || undefined,
  meta: { cellClass: 'whitespace-nowrap' },
  cell: ({ row }) => (
    <Link
      to={`/orders/${row.original.id}`}
      className="text-on-surface font-medium hover:text-primary transition-colors duration-150 font-mono"
    >
      {row.original.so_number}
    </Link>
  ),
};

// The link is conditional on `customer_org_id`, and the two branches read
// different fields on purpose: an order attached to an organization shows the
// org name and falls back to the contact, while a loose order has only the
// contact to show. Do not "simplify" the branches into one expression.
const CUSTOMER_COLUMN: ColumnDef<SalesOrder, any> = {
  id: 'customer',
  header: 'Customer',
  accessorFn: (o) => o.org_name || o.customer_name || undefined,
  meta: { cellClass: 'text-on-surface' },
  cell: ({ row }) =>
    row.original.customer_org_id ? (
      <Link
        to={`/customers/${row.original.customer_org_id}`}
        className="hover:text-white transition-colors duration-150"
      >
        {row.original.org_name || row.original.customer_name || '\u2014'}
      </Link>
    ) : (
      row.original.customer_name || '\u2014'
    ),
};

const STATUS_COLUMN: ColumnDef<SalesOrder, any> = {
  id: 'status',
  header: 'Status',
  meta: { align: 'center' },
  // Part shipped reads as the status itself; the stored status stays underneath for tabs and filters.
  cell: ({ row }) => {
    const o = row.original;
    // The reading replaces the pill while partly shipped, and on a sale once something came back
    // ("Shipped, 1 returned"); a lease keeps its pill, which carries the round trip's own stages.
    const showReading = o.shipping?.state === 'partly' || (o.order_type === 'sale' && (o.shipping?.returned ?? 0) > 0);
    return showReading ? <ReadingBadge reading={o.shipping} /> : <StatusPill status={o.status} />;
  },
};

const SHIP_DATE_COLUMN: ColumnDef<SalesOrder, any> = {
  id: 'ship_date',
  header: 'Ship Date',
  accessorFn: (o) => o.ship_date || undefined,
  meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
  cell: ({ row }) =>
    row.original.ship_date ? formatEventDate(row.original.ship_date) : '\u2014',
};

// The quantities the stats card adds up, per order. Which item counts where is
// the `stat` field in itemTypes.ts. Zero sorts as empty (contract 2).
const itemColumn = (stat: OrderStat, header: string): ColumnDef<SalesOrder, any> => ({
  id: stat,
  header,
  accessorFn: (o) => statTotalsFor(INSTANCE, o.lines || [])[stat] || undefined,
  meta: { align: 'right', numeric: true },
  cell: ({ getValue }) => {
    const n = getValue() as number | undefined;
    return n ? n.toLocaleString('en-US') : <span className="text-on-surface-disabled">{'\u2014'}</span>;
  },
});

const ITEM_COLUMNS: ColumnDef<SalesOrder, any>[] = [
  itemColumn('drones', 'Drones'),
  itemColumn('batteries', 'Batteries'),
  itemColumn('chargers', 'Chargers'),
  itemColumn('ground', 'Ground kit'),
];

// A due date, plus how far off it is while the order still sits in one of its
// tab's stages. A shipped order's past delivery date is history, not lateness.
function dueCell(o: SalesOrder, type: OrderType, fmt?: string) {
  const tabCfg = TABS.find(t => t.key === type)!;
  const due = o[tabCfg.dateCol];
  if (!due) return '\u2014';
  const text = formatEventDate(due, fmt);
  if (!tabCfg.stages.includes(o.status)) return text;
  const days = daysFromToday(due, businessToday().format('YYYY-MM-DD'));
  const tone = days < 0 ? 'text-red-400 font-medium' : days <= 7 ? 'text-primary' : 'text-on-surface';
  return (
    <>
      <span className={tone}>{text}</span>{' '}
      <span className="text-on-surface-variant">{relativeDays(days)}</span>
    </>
  );
}

const CREATOR_COLUMN: ColumnDef<SalesOrder, any> = {
  id: 'creator',
  header: 'By',
  meta: { cellClass: 'text-on-surface-variant' },
  cell: ({ row }) => row.original.creator_firstname || '\u2014',
};

const SALE_COLUMNS: ColumnDef<SalesOrder, any>[] = [
  ORDER_NUMBER_COLUMN,
  CUSTOMER_COLUMN,
  STATUS_COLUMN,
  {
    id: 'delivery_date',
    header: 'Delivery Date',
    accessorFn: (o) => o.delivery_date || undefined,
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => dueCell(row.original, 'sale'),
  },
  SHIP_DATE_COLUMN,
  ...ITEM_COLUMNS,
  CREATOR_COLUMN,
];

const LEASE_COLUMNS: ColumnDef<SalesOrder, any>[] = [
  ORDER_NUMBER_COLUMN,
  CUSTOMER_COLUMN,
  STATUS_COLUMN,
  {
    id: 'effective_date',
    header: 'Effective',
    accessorFn: (o) => o.effective_date || undefined,
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => dueCell(row.original, 'lease'),
  },
  {
    // No end date on a lease means it never ends, which is a fact worth saying
    // rather than an em-dash placeholder standing in for a missing value.
    id: 'end_date',
    header: 'End Date',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) =>
      row.original.end_date ? (
        formatEventDate(row.original.end_date)
      ) : (
        <span className="text-on-surface-variant italic">Permanent</span>
      ),
  },
  SHIP_DATE_COLUMN,
  ...ITEM_COLUMNS,
  CREATOR_COLUMN,
];

const RENTAL_COLUMNS: ColumnDef<SalesOrder, any>[] = [
  ORDER_NUMBER_COLUMN,
  CUSTOMER_COLUMN,
  STATUS_COLUMN,
  {
    // A rental is short, so both ends drop the year and read as a span.
    id: 'effective_date',
    header: 'Start',
    accessorFn: (o) => o.effective_date || undefined,
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => dueCell(row.original, 'rental', 'MMM D'),
  },
  {
    id: 'end_date',
    header: 'End',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) =>
      row.original.end_date ? formatEventDate(row.original.end_date, 'MMM D') : '\u2014',
  },
  SHIP_DATE_COLUMN,
  ...ITEM_COLUMNS,
  CREATOR_COLUMN,
];

const COLUMNS_BY_TAB: Record<OrderType, ColumnDef<SalesOrder, any>[]> = {
  sale: SALE_COLUMNS,
  lease: LEASE_COLUMNS,
  rental: RENTAL_COLUMNS,
};

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
const OrdersPage: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const location = useLocation();
  const history = useHistory();

  // Determine initial tab from URL hash or default to sales
  const hashTab = location.hash.replace('#', '') as OrderType;
  const initialTab = TABS.find(t => t.key === hashTab)?.key || 'sale';

  const [activeTab, setActiveTab] = useState<OrderType>(initialTab);
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed load leaves `orders` empty, and a card reading "0 orders" would
  // pass that off as no open work. The card hides instead.
  const [loadFailed, setLoadFailed] = useState(false);
  const [pointedId, setPointedId] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({ sales: 0, leases: 0, rentals: 0 });

  // Per-tab state (in-memory, resets on reload)
  const [tabStates, setTabStates] = useState<Record<string, TabState>>({
    sale: DEFAULT_TAB_STATE,
    lease: DEFAULT_TAB_STATE,
    rental: DEFAULT_TAB_STATE,
  });

  const currentState = tabStates[activeTab] || DEFAULT_TAB_STATE;

  const updateTabState = useCallback((updates: Partial<TabState>) => {
    setTabStates(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], ...updates },
    }));
  }, [activeTab]);

  // Fetch all orders and counts
  //
  // `ignore` drops a response that lands after the tab moved on. Without it, a
  // quick Sales -> Leases -> Rentals lands Sales' rows under Rentals, and the
  // stats card counts them as Rentals.
  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const [ordersData, countsData] = await Promise.all([
          api.get(`/orders?order_type=${activeTab}&include=lines`),
          api.get('/orders/counts'),
        ]);
        if (ignore) return;
        setOrders(ordersData || []);
        setCounts(countsData || { sales: 0, leases: 0, rentals: 0 });
      } catch (error) {
        if (ignore) return;
        setLoadFailed(true);
        toast.error('Failed to load orders');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // The rows and the column set have to move together. `activeTab` flips
  // synchronously, so without clearing `orders` here the previous tab's rows
  // sit under the new tab's columns until the refetch lands, and the status
  // chips count the wrong tab's rows. Clearing the rows AND raising `loading`
  // in the same batch is what makes the vessel show its loading state instead:
  // rows-only would flash an empty table for a frame, since the fetch effect
  // does not run until after this commit paints.
  //
  // The guard covers only the state clears, never the hash write. Clearing the
  // rows for the tab that is already up would hang on the spinner forever: the
  // fetch effect keys off `activeTab`, which did not change, so nothing would
  // ever refill them. (`Segmented` does not call this for the segment already
  // on, so today the guard is belt to that braces; it stays because this
  // function does not get to assume who calls it.)
  const handleTabChange = (tab: OrderType) => {
    history.replace(`/orders#${tab}`);
    if (tab === activeTab) return;
    setActiveTab(tab);
    setOrders([]);
    setLoading(true);
  };

  const tab = TABS.find(t => t.key === activeTab)!;

  const isOpen = useCallback((o: SalesOrder) => !tab.closed.includes(o.status), [tab]);
  const openCount = orders.filter(isOpen).length;

  // Filter only — the table owns the order now. Deps are the fields this reads
  // rather than the whole `currentState`, so a sort change does not mint a new
  // data array for the table to rebuild its row model from.
  //
  // The Open view hides closed orders until there is a search: a number or a
  // customer typed there must still find the order that shipped last year.
  const filtered = useMemo(() => {
    let list = [...orders];

    if (currentState.statusFilters.length > 0) {
      list = list.filter(o => currentState.statusFilters.includes(o.status));
    } else if (!currentState.search) {
      list = list.filter(isOpen);
    }
    if (currentState.search) {
      const term = currentState.search.toLowerCase();
      list = list.filter(o =>
        (o.so_number || '').toLowerCase().includes(term) ||
        (o.org_name || o.customer_name || '').toLowerCase().includes(term) ||
        (o.notes || '').toLowerCase().includes(term)
      );
    }

    return list;
  }, [orders, currentState.statusFilters, currentState.search, isOpen]);

  // Read at MOUNT only (the vessel seeds its own sort state from it), so a
  // fresh array identity per render costs nothing. The fallback is the tab's
  // own date column, which is exactly what the hand-rolled sort did with
  // `sortCol || tab.dateCol`.
  const initialSorting: SortingState = [
    { id: currentState.sortCol || tab.dateCol, desc: currentState.sortDir === 'desc' },
  ];

  // The sort readback. Deliberately NOT `updateTabState`: this closes over the
  // tab it was built for, so a write can only ever land in that tab's slot.
  // The table remounts (key) in the same commit that flips `activeTab`, so a
  // mounted table and this closure always name the same tab — including for
  // the mount echo, which therefore rewrites the tab its own value and bails
  // below without a render.
  const handleSortingChange = useCallback((sorting: SortingState) => {
    const tabKey = activeTab;
    const next = sorting[0];
    const sortCol = next ? next.id : '';
    const sortDir: 'asc' | 'desc' = next?.desc ? 'desc' : 'asc';
    setTabStates(prev => {
      const held = prev[tabKey] || DEFAULT_TAB_STATE;
      if (held.sortCol === sortCol && held.sortDir === sortDir) return prev;
      return { ...prev, [tabKey]: { ...held, sortCol, sortDir } };
    });
  }, [activeTab]);

  const countKey = (t: OrderType) => t === 'sale' ? 'sales' : t === 'lease' ? 'leases' : 'rentals';

  return (
    <PageLayout kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Orders"
        lede="Sales, leases and rentals, one tab each."
        helpTitle="About orders"
        help={
          <>
            <p>
              An order is the one record of equipment leaving Firefly for a customer, in three
              kinds. A sale (SO-) sells it outright and is complete once it has shipped. A lease
              (LO-) places drones with a customer for the long term and stays delivered until
              its adjustments end it. A rental (RO-) is a short booking against the available
              fleet and comes back: delivered, awaiting return, returned.
            </p>
            <p>
              The three tabs keep their own filters and counts. Leases lower the fleet the
              forecast starts from; rentals appear beside events as commitments against it.
            </p>
            <p>
              Each tab opens on its open orders. Shipped, returned and cancelled ones sit
              behind their own status, and a search finds them from the Open view too. The
              card above the table totals whatever the table is showing, and an order still
              in progress turns red once its delivery or start date has passed.
            </p>
            <p>
              Every kind can carry any equipment: drones, batteries, chargers, ground stations,
              cases.
            </p>
          </>
        }
        primary={<PrimaryAction to={tab.newLink}>{tab.newLabel}</PrimaryAction>}
      />

      {/* Which pile. NAVIGATION, not a filter: it changes which list is on
          screen (and which columns it wears), so it sits above the bar rather
          than inside it. The count rides the label as quiet text rather than a
          badge: three gold pills would spend the accent the primary action
          already has, and none of these counts is a failure waiting on
          somebody. */}
      <Segmented
        label="Which orders to show"
        options={TABS.map(t => {
          const count = counts[countKey(t.key)] || 0;
          return {
            key: t.key,
            label: t.label,
            count: count > 0 ? count : undefined,
          };
        })}
        value={activeTab}
        onChange={(k) => handleTabChange(k as OrderType)}
      />

      <FilterBar>
        <FilterSearch
          value={currentState.search}
          onChange={search => updateTabState({ search })}
          label="Search orders by number, customer or notes"
          placeholder="Search orders..."
        />
        {/* Eight segments at their widest, so they get the line to themselves
            rather than pushing the search around as the tab changes. */}
        <FilterBreak />
        {/* MULTI, because "delivered plus awaiting return" is a real question.
            The Open segment is not a status: it is the empty filter, which is
            why it is keyed apart from every value the rows can hold. Its count
            matches the tab badge, since both leave out `tab.closed`. */}
        <Segmented
          multi
          label="Which order statuses to show"
          options={[
            {
              key: OPEN_STATUSES,
              label: 'Open',
              count: openCount,
            },
            ...tab.statuses.map(status => ({
              key: status,
              label: <span className="capitalize">{status.replace(/_/g, ' ')}</span>,
              count: orders.filter(o => o.status === status).length,
            })),
          ]}
          value={currentState.statusFilters.length === 0 ? [OPEN_STATUSES] : currentState.statusFilters}
          onChange={status => {
            if (status === OPEN_STATUSES) {
              updateTabState({ statusFilters: [] });
              return;
            }
            const held = currentState.statusFilters;
            updateTabState({
              statusFilters: held.includes(status)
                ? held.filter(x => x !== status)
                : [...held, status],
            });
          }}
        />
      </FilterBar>

      {/* Totals over the rows the table shows, so it sits between the filters
          and the table it sums. */}
      {!loadFailed && (
        <OrderStats
          orders={filtered}
          stages={tab.stages}
          dueField={tab.dateCol}
          loading={loading}
          onHighlight={setPointedId}
          onReveal={(id) =>
            document.querySelector(`[data-order-row="${id}"]`)?.scrollIntoView({
              block: 'center',
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            })
          }
        />
      )}

      {/* The vessel is the DataTable's: no page wrapper, no header styling, no
          colSpan arithmetic. `key={activeTab}` is load-bearing twice over: it
          is what gives each tab its own sort state to seed and write back, and
          a full unmount is what stickyHeader wants anyway, since it measures
          its own top edge and this page's chip row is a different height per
          tab. */}
      <DataTable<SalesOrder>
        key={activeTab}
        data={filtered}
        columns={COLUMNS_BY_TAB[activeTab]}
        getRowId={getRowId}
        sortable
        stickyHeader
        initialSorting={initialSorting}
        onSortingChange={handleSortingChange}
        rowProps={(o) => ({
          'data-order-row': o.id,
          className: o.id === pointedId ? POINTED_ROW : undefined,
        })}
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading orders" className="space-y-2 px-4 py-6">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </SkeletonGroup>
        }
        empty={
          <EmptyState variant="area">No {tab.label.toLowerCase()} found</EmptyState>
        }
      />
    </PageLayout>
  );
};

export default OrdersPage;
